import React, { useState, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { studentGroupColumnDefs } from './_utils/studentGroupColumn';
import {
  getStudentGroups,
  getStudentGroupGlobalData,
} from '../../services/apiService';
import StudentGroupModal from './_components/StudentGroupModal';
import StudentGroupEditModal from './_components/StudentGroupEditModal';
import { styledDefaultColDef } from '../../extras/cellRenderers';
import { PageHeader } from '../../extras/PageLayout';

const StudentGroups = () => {
  const [, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [createGlobals, setCreateGlobals] = useState(null);
  const [, setPrefetchingGlobals] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const pageSize = 10;

  const handleOpenStudentGroups = () => {
    setOpenCreateModal(true);
  };

  useEffect(() => {
    let isActive = true;
    const fetchGlobals = async () => {
      try {
        setPrefetchingGlobals(true);
        const res = await getStudentGroupGlobalData();
        const data = res?.data || res;
        if (!isActive) return;
        setCreateGlobals({
          cohort: Array.isArray(data?.cohort) ? data.cohort : [],
          almaXCohort: Array.isArray(data?.almaXCohort) ? data.almaXCohort : [],
          program: {
            course: Array.isArray(data?.program?.course)
              ? data.program.course
              : [],
            plan: Array.isArray(data?.program?.plan) ? data.program.plan : [],
            subplan: Array.isArray(data?.program?.subplan)
              ? data.program.subplan
              : [],
          },
        });
      } catch (e) {
        console.error('Failed to fetch global data for StudentGroupModal', e);
      } finally {
        if (isActive) setPrefetchingGlobals(false);
      }
    };
    fetchGlobals();
    return () => {
      isActive = false;
    };
  }, []);

  const getServerSideDatasource = () => {
    return {
      getRows: async (params) => {
        // Calculate page number from startRow
        const { startRow } = params.request || params;
        const page = Math.floor((startRow || 0) / pageSize) + 1;

        setLoading(true);

        try {
          // Call your service function - modify the API call to include page parameter
          const response = await getStudentGroups(page);

          if (response && response.data) {
            // Map the data to ensure all required fields are present
            const mappedData = response.data?.data?.map((item) => ({
              id: item.id,
              groupName: item.groupName || 'N/A',
              groupType:
                item.groupType && item.params?.type
                  ? `${item.groupType}-${item.params.type}`
                  : item.groupType || 'N/A',
              params: item.params || {},
              groupDecr: item.groupDecr || 'N/A',
              createdAt: item.createdAt || 'N/A',
              createdBy: item.createdBy || 'N/A',
              updatedAt: item.updatedAt || 'N/A',
              updatedBy: item.updatedBy || 'N/A',
              deletedAt: item.deletedAt || null,
              contactDate: item.contactDate || null,
            }));

            // Get total count for pagination
            const totalRows = response.data?.totalRecords;

            params.successCallback(mappedData, totalRows);
          } else {
            params.successCallback([], 0);
          }
        } catch (error) {
          console.error('Error fetching student groups:', error);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    };
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    const datasource = getServerSideDatasource();
    params.api.setServerSideDatasource(datasource);
    params.api.sizeColumnsToFit();
  };

  const agGridColumnDefs = useMemo(() => {
    return studentGroupColumnDefs({
      setOpenEditModal,
      setSelectedRowData, // Pass this function to the column definitions
    });
  }, []);

  const refreshData = () => {
    if (gridApi) {
      const datasource = getServerSideDatasource();
      gridApi.setServerSideDatasource(datasource);
    }
  };

  const defaultColDef = useMemo(
    () => ({
      ...styledDefaultColDef,
      filter: true,
    }),
    []
  );

  useEffect(() => {
    // openEditModal state tracked for modal lifecycle
  }, [openEditModal]);

  // Reset selectedRowData when modal closes
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedRowData(null);
  };

  return (
    <div>
      {/* Page header */}
      <PageHeader
        title="Student Groups"
        subtitle="Create and manage student groups"
        actions={
          <button
            onClick={handleOpenStudentGroups}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              backgroundColor: 'var(--primary-main, #4F46E5)',
              border: 'none',
              borderRadius: 4,
              padding: '8px 16px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
            }}
          >
            + Create Student Group
          </button>
        }
      />

      {openCreateModal && (
        <StudentGroupModal
             onRefresh={refreshData}
          openCreateModal={openCreateModal}
          setOpenCreateModal={setOpenCreateModal}
          cohortListProp={createGlobals?.cohort || []}
          almaxCohortListProp={createGlobals?.almaXCohort || []}
          programOptionsProp={
            createGlobals?.program || { course: [], plan: [], subplan: [] }
          }
        />
      )}

      {/* Pass both the modal state and row data to the edit modal */}
      {openEditModal && (
        <StudentGroupEditModal
          openEditModal={openEditModal}
          setOpenEditModal={handleCloseEditModal}
          onRefresh={refreshData}
          rowData={selectedRowData}
          cohortListProp={createGlobals?.cohort || []}
          almaxCohortListProp={createGlobals?.almaXCohort || []}
          programOptionsProp={
            createGlobals?.program || { course: [], plan: [], subplan: [] }
          }
        />
      )}

      {/* Table */}
      <div style={{ padding: '0 0 24px' }}>
        <div
          style={{
            backgroundColor: 'var(--card-background, #fff)',
            borderRadius: 16,
            border: '1px solid var(--border-light, rgba(0,0,0,0.07))',
            boxShadow: 'var(--shadow-sm, 0 1px 6px rgba(0,0,0,0.05))',
            overflow: 'hidden',
          }}
        >
          <div
            className='ag-theme-alpine'
            style={{ height: 630, width: '100%' }}
          >
            <AgGridReact
              columnDefs={agGridColumnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              suppressRowClickSelection={true}
              rowSelection='multiple'
              tooltipShowDelay={500}
              tooltipHideDelay={2000}
              enableBrowserTooltips={true}
              rowHeight={52}
              rowModelType='serverSide'
              serverSideStoreType='partial'
              animateRows={true}
              serverSideInfiniteScroll={true}
              pagination={true}
              paginationPageSize={pageSize}
              cacheBlockSize={pageSize}
              suppressPaginationPanel={false}
              domLayout='normal'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentGroups;
